import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Eye, Heart, MousePointer, Share2,
  ThumbsUp, MessageSquare, TrendingUp, TrendingDown, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const EngagementMetrics = ({ postAnalytics, analyticsLoading, formatNumber }) => {
  return (
    <Card className="border-slate-100" data-testid="post-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-emerald-500" />
          Performance Stats
          {postAnalytics?.metrics && (
            <Badge 
              variant="secondary" 
              className={`ml-auto text-xs ${postAnalytics.comparison?.engagement_vs_avg > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
            >
              {postAnalytics.comparison?.engagement_vs_avg > 0 ? (
                <><TrendingUp className="h-3 w-3 mr-1" /> Above avg</>
              ) : (
                <><TrendingDown className="h-3 w-3 mr-1" /> Below avg</>
              )}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {analyticsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : postAnalytics?.metrics ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-blue-50 text-center">
                <Eye className="h-4 w-4 text-blue-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{formatNumber(postAnalytics.metrics.reach)}</p>
                <p className="text-xs text-slate-500">Reach</p>
              </div>
              <div className="p-3 rounded-lg bg-pink-50 text-center">
                <Heart className="h-4 w-4 text-pink-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{formatNumber(postAnalytics.metrics.engagement)}</p>
                <p className="text-xs text-slate-500">Engagement</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 text-center">
                <MousePointer className="h-4 w-4 text-emerald-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{formatNumber(postAnalytics.metrics.clicks)}</p>
                <p className="text-xs text-slate-500">Clicks</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 text-center">
                <Share2 className="h-4 w-4 text-purple-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{formatNumber(postAnalytics.metrics.shares)}</p>
                <p className="text-xs text-slate-500">Shares</p>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Engagement Breakdown</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3 text-blue-500" />
                  <span className="text-slate-600">{formatNumber(postAnalytics.metrics.likes)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3 text-emerald-500" />
                  <span className="text-slate-600">{formatNumber(postAnalytics.metrics.comments)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Share2 className="h-3 w-3 text-purple-500" />
                  <span className="text-slate-600">{formatNumber(postAnalytics.metrics.shares)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span className="text-sm text-slate-600">Engagement Rate</span>
              <Badge className={`${postAnalytics.metrics.engagement_rate > 5 ? 'bg-emerald-100 text-emerald-700' : postAnalytics.metrics.engagement_rate > 2 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                {postAnalytics.metrics.engagement_rate}%
              </Badge>
            </div>

            {postAnalytics.comparison && (
              <div className="p-3 rounded-lg bg-gradient-to-r from-slate-50 to-emerald-50 border border-slate-100">
                <p className="text-xs text-slate-500 mb-2">vs. Your Average</p>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  {['reach', 'engagement', 'clicks'].map((metric) => (
                    <div key={metric}>
                      <span className={`font-semibold ${postAnalytics.comparison[`${metric}_vs_avg`] >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {postAnalytics.comparison[`${metric}_vs_avg`] >= 0 ? '+' : ''}{postAnalytics.comparison[`${metric}_vs_avg`]}%
                      </span>
                      <p className="text-slate-400 capitalize">{metric}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500 text-sm">No analytics data available yet</div>
        )}
        <Link to="/analytics?tab=posts">
          <Button variant="outline" className="w-full mt-4" size="sm">View Full Analytics</Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default EngagementMetrics;
